import { IDocument } from '@faire/web-api--source/indigofair/data/IDocument';
import { ISO3166Alpha3 } from '@faire/web-api--source/indigofair/iso3166/ISO3166Alpha3';

export namespace IBusinessIdentifier {
  export enum IdentifierType {
    VAT = 'VAT',
    EORI = 'EORI',
    CANADA_SALES_TAX = 'CANADA_SALES_TAX',
    /**
     * SIREN numbers are trade registry numbers required by the French government on a brand's order invoices
     */
    SIREN = 'SIREN',
    /**
     * KVK numbers are dutch business registration numbers, we show Faire's KVK number on commission invoices
     */
    KVK = 'KVK',
    /**
     * Australian Business Register number, mandatory for all businesses in AUS
     */
    ABN = 'ABN',
    /**
     * Optional Australian goods and services tax number. This will have the same numerical value as the ABN.
     */
    AUSTRALIA_GST = 'AUSTRALIA_GST',
    /**
     * Brands that ship to Germany need to register for a LUCID number to verify they are compliant with German packaging laws
     */
    LUCID = 'LUCID',
    /**
     * Optional Canadian goods and services tax/harmonized sales tax registration number
     */
    CANADA_GST = 'CANADA_GST',
    /**
     * Optional Canadian Quebec sales tax registration number
     */
    CANADA_QST = 'CANADA_QST',
    /**
     * Alcohol Wholesaler Registration Scheme, required for UK based brands to sell alcohol
     */
    AWRS_URN = 'AWRS_URN',
    /**
     * Australian Registration Number
     */
    ARN = 'ARN',
    /**
     * Canadian business number collected during sign up
     */
    CANADA_BUSINESS_NUMBER = 'CANADA_BUSINESS_NUMBER',
    /**
     * business number for countries outside of NAM collected during sign up
     */
    GLOBAL_BUSINESS_NUMBER = 'GLOBAL_BUSINESS_NUMBER',
    /**
     * Generic identifier for IDV purposes
     */
    GENERIC_IDV_IDENTIFIER = 'GENERIC_IDV_IDENTIFIER',
  }

  export enum IdentifierState {
    /**
     * This is a valid identifier and can be used
     */
    VALID = 'VALID',
    /**
     * This identifier has been marked as deleted, and should not be used.
     */
    DELETED = 'DELETED',
    /**
     * This identifier has been requested but the retailer/brand did not have the identifier
     */
    REQUESTED = 'REQUESTED',
  }

  /**
   * Return an instance of IBusinessIdentifier with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (
    partial?: Partial<IBusinessIdentifier>
  ): IBusinessIdentifier =>
    ({
      ...(partial ?? {}),
    }) as IBusinessIdentifier;
}

/**
 * Generic business identification numbers used for tax, import, resell identification
 * Able to be added to both retailers and brands
 * Kept generic so we can scale this to multiple countries
 */
export interface IBusinessIdentifier {
  identifier_type: keyof typeof IBusinessIdentifier.IdentifierType | undefined;
  identifier_value: string | undefined;
  state: keyof typeof IBusinessIdentifier.IdentifierState | undefined;
  country: keyof typeof ISO3166Alpha3 | undefined;
  document: IDocument | undefined;
}
